import { useTranslation } from "react-i18next";
import { Languages } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { apiClient } from "@/services/api";
import { useAuth } from "@/hooks/useAuth";

interface Language {
  code: string;
  name: string;
  nativeName: string;
}

const LANGUAGES: Language[] = [
  { code: "en", name: "English", nativeName: "English" },
  { code: "ko", name: "Korean", nativeName: "한국어" },
];

interface LanguageSwitcherProps {
  /** Trigger colors for the surface it sits on; the menu itself stays light. */
  tone?: "light" | "dark";
}

export default function LanguageSwitcher({
  tone = "light",
}: LanguageSwitcherProps) {
  const { t, i18n } = useTranslation("common");
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentLanguage =
    LANGUAGES.find((lang) => lang.code === i18n.language) || LANGUAGES[0];

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const changeLanguage = async (languageCode: string) => {
    i18n.changeLanguage(languageCode);
    setIsOpen(false);

    // Sync with backend by setting cookie
    document.cookie = `i18next=${languageCode}; path=/; max-age=31536000; SameSite=Lax`;

    // Persist locale in the database for authenticated users
    if (user) {
      try {
        await apiClient.updateLocale(languageCode);
      } catch {
        // Locale update is best-effort; don't interrupt the UI
      }
    }
  };

  return (
 <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
 className={`flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-2 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 ${
          tone === "dark"
            ? "text-gray-300 hover:text-white"
            : "text-secondary-500 hover:text-secondary-700"
        }`}
        aria-label={t("selectLanguage")}
        data-testid="language-switcher"
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
 <Languages className="h-4 w-4 sm:h-5 sm:w-5" />
 <span className="hidden sm:inline">{currentLanguage.nativeName}</span>
 <span className="sm:hidden">{currentLanguage.code.toUpperCase()}</span>
      </button>

      {isOpen && (
        <div
 className="absolute right-0 mt-2 w-40 rounded-lg shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50"
          role="menu"
          aria-orientation="vertical"
        >
 <div className="py-1">
            {LANGUAGES.map((language) => (
              <button
                key={language.code}
                onClick={() => changeLanguage(language.code)}
 className={`block w-full text-left px-4 py-2 text-sm hover:bg-secondary-100 ${
                  currentLanguage.code === language.code
                    ? "bg-secondary-50 text-primary-600 font-medium"
                    : "text-secondary-700"
                }`}
                role="menuitem"
              >
 <div className="flex items-center justify-between">
                  <span>{language.nativeName}</span>
                  {currentLanguage.code === language.code && (
 <span className="text-primary-600">✓</span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
